import { Capacitor } from '@capacitor/core';
import React, { createContext, type ReactNode, useCallback, useEffect, useState } from 'react';

export type DesktopContextType = {
  isDesktop: boolean;
  forceDesktopMode: boolean;
  toggleDesktopMode: () => void;
  showForceDesktopSwitch: boolean;
};

export const DesktopContext = createContext<DesktopContextType>({
  isDesktop: false,
  forceDesktopMode: false,
  showForceDesktopSwitch: false,
  toggleDesktopMode: () => {
    // empty
  },
});

const isMobileDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileUA = /iphone|ipad|ipod|android|webos|blackberry|iemobile|opera mini|mobile|tablet/i.test(userAgent);

  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const isSmallScreen = window.innerWidth <= 1024 || window.innerHeight <= 1024;

  // kombiniere beides
  return isMobileUA || (isTouchDevice && isSmallScreen);
};

export const IsDesktopContextProvider = ({ children }: { children: ReactNode }) => {
  const isNativ = Capacitor.isNativePlatform();

  const [isDesktop, setDesktop] = useState(window.innerWidth > 1024 && !isMobileDevice() && !isNativ);
  const [forceDesktopMode, setForceDesktopMode] = useState(false);
  const handleResize = useCallback(() => {
    const isMobile = isMobileDevice();
    const isNativ = Capacitor.isNativePlatform();

    const isNowDesktop = forceDesktopMode || (window.innerWidth > 1024 && !isMobile && !isNativ);

    setDesktop(isNowDesktop);
    if (isNowDesktop) {
      document.documentElement.classList.add('is-desktop');
    } else {
      document.documentElement.classList.remove('is-desktop');
    }
  }, [forceDesktopMode]);

  const toggleDesktopMode = useCallback(() => {
    setForceDesktopMode((prev) => !prev);
    handleResize();
  }, [handleResize]);

  useEffect(() => {
    handleResize();

    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 300);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [handleResize]);

  useEffect(() => {
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      if (forceDesktopMode) {
        viewportMeta.setAttribute('content', 'width=1200, initial-scale=1, user-scalable=yes');
      } else {
        viewportMeta.setAttribute(
          'content',
          'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no',
        );
      }
    }
  }, [forceDesktopMode]);

  const showForceDesktopSwitch = !isNativ && (forceDesktopMode || !isDesktop);

  return (
    <DesktopContext.Provider value={{ isDesktop, toggleDesktopMode, forceDesktopMode, showForceDesktopSwitch }}>
      {children}
    </DesktopContext.Provider>
  );
};
